/**
 * <infinite-scroll> — progressive-enhancement pagination for Dawn-based themes.
 *
 * Debug: open the browser console and run
 *   customElements.get('infinite-scroll')
 * If that returns `undefined`, this file is not loading at all (check that the
 * asset is named exactly `infinite-scroll.js` and that the Network tab does not
 * show a 404 for it).
 */
class InfiniteScroll extends HTMLElement {
  constructor() {
    super();
    this.loading = false;
    this.autoLoaded = 0;
    this.onClick = this.onClick.bind(this);
  }

  connectedCallback() {
    this.mode = this.dataset.mode === 'load_more' ? 'load_more' : 'infinite';
    this.maxAutoPages = parseInt(this.dataset.maxAutoPages || '0', 10);
    this.sentinel = this.querySelector('.infinite-scroll__sentinel');
    this.status = this.querySelector('.infinite-scroll__status');

    if (!this.dataset.nextUrl) {
      console.warn('[infinite-scroll] no data-next-url on the element, nothing to load.');
      return;
    }

    this.classList.add('infinite-scroll--js');

    // Delegated click: works even if the button markup changes or is re-rendered.
    this.addEventListener('click', this.onClick);

    if (this.mode === 'infinite' && 'IntersectionObserver' in window) {
      this.classList.add('infinite-scroll--auto');
      this.observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) this.load();
        },
        { rootMargin: '600px 0px' }
      );
      if (this.sentinel) {
        this.observer.observe(this.sentinel);
      } else {
        console.warn('[infinite-scroll] sentinel missing, falling back to the button.');
        this.classList.remove('infinite-scroll--auto');
      }
    }
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
    this.removeEventListener('click', this.onClick);
  }

  onClick(event) {
    const trigger = event.target.closest('.infinite-scroll__button');
    if (!trigger || !this.contains(trigger)) return;
    event.preventDefault();
    this.load(true);
  }

  get button() {
    return this.querySelector('.infinite-scroll__button');
  }

  // Resolved lazily: the grid may be re-rendered by facets.js between loads.
  get grid() {
    const scope = this.closest('.collection') || document;
    return scope.querySelector('#product-grid') || document.getElementById('product-grid');
  }

  get strings() {
    return {
      loading: this.dataset.loadingText || 'Loading more products',
      loaded: this.dataset.loadedText || 'More products loaded',
      error: this.dataset.errorText || 'Could not load more products',
    };
  }

  announce(message) {
    if (this.status) this.status.textContent = message;
  }

  showButton() {
    this.setAttribute('data-show-button', '');
  }

  async load(fromClick = false) {
    const url = this.dataset.nextUrl;
    if (this.loading || !url) return;

    // In auto mode, stop auto-loading after N pages so the footer stays reachable.
    if (!fromClick && this.maxAutoPages > 0 && this.autoLoaded >= this.maxAutoPages) {
      this.showButton();
      return;
    }

    const grid = this.grid;
    if (!grid) {
      console.warn('[infinite-scroll] could not find #product-grid, leaving the link alone.');
      return;
    }

    this.loading = true;
    this.setAttribute('aria-busy', 'true');
    this.classList.add('infinite-scroll--loading');
    this.announce(this.strings.loading);

    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error(`Request failed: ${response.status}`);

      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const newGrid = doc.getElementById('product-grid');
      const items = newGrid ? Array.from(newGrid.children) : [];

      if (items.length) {
        const fragment = document.createDocumentFragment();
        items.forEach((item) => {
          item.querySelectorAll('img[loading="eager"]').forEach((img) => {
            img.setAttribute('loading', 'lazy');
          });
          fragment.appendChild(item);
        });
        grid.appendChild(fragment);
        this.revealAnimations(grid, items);
        this.announce(this.strings.loaded);
      }

      const nextEl = doc.querySelector('infinite-scroll');
      const nextUrl = nextEl ? nextEl.dataset.nextUrl : '';

      if (nextUrl) {
        this.dataset.nextUrl = nextUrl;
        if (this.button) this.button.href = nextUrl;
      } else {
        this.complete();
      }

      if (!fromClick) this.autoLoaded += 1;
    } catch (error) {
      console.error('[infinite-scroll]', error);
      // Fall back to a plain link the shopper can click.
      this.classList.remove('infinite-scroll--auto');
      this.showButton();
      this.announce(this.strings.error);
      if (this.observer) this.observer.disconnect();
    } finally {
      this.loading = false;
      this.removeAttribute('aria-busy');
      this.classList.remove('infinite-scroll--loading');
      this.removeAttribute('data-show-button');
      if (this.mode === 'infinite') {
        requestAnimationFrame(() => this.maybeLoadMore());
      }
    }
  }

  // The sentinel can still be in view after appending (short pages, tall viewport).
  maybeLoadMore() {
    if (!this.dataset.nextUrl || !this.sentinel || this.loading) return;
    if (this.maxAutoPages > 0 && this.autoLoaded >= this.maxAutoPages) {
      this.showButton();
      return;
    }
    const rect = this.sentinel.getBoundingClientRect();
    if (rect.top < window.innerHeight + 600) this.load();
  }

  complete() {
    this.dataset.nextUrl = '';
    if (this.observer) this.observer.disconnect();
    this.hidden = true;
  }

  revealAnimations(grid, items) {
    if (typeof initializeScrollAnimationTrigger === 'function') {
      try {
        initializeScrollAnimationTrigger(grid);
        return;
      } catch (error) {
        /* fall through */
      }
    }
    items.forEach((item) => item.classList.add('scroll-trigger--cancel'));
  }
}

if (!customElements.get('infinite-scroll')) {
  customElements.define('infinite-scroll', InfiniteScroll);
}